import {
  Box,
  Button,
  Switch,
  Text,
  Flex,
  Image,
  VStack,
  HStack,
  useToast,
  Divider
} from '@chakra-ui/react';
import { FC, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { seasonSchema, SeasonFormData } from '@olagg/validation-schemas/season.schema';
import { AdminSeason } from '@olagg/db-types';
import OlaGGFormInput from '../../components/Forms/OlaggFormInput';
import OlaggForm from '../../components/Forms/OlaggForm';
import OlaggFormSelect from '../../components/Forms/OlaggFormSelect';
import { fromLocalDateTimeToUTC, toLocalTimezone } from '../../utils';
import useSeasonChallengeTasks from './challenges/tasks/hooks/useSeasonChallengeTasks';

type IProps = {
  initialData?: AdminSeason | undefined;
  onSubmit: (data: SeasonFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

type MultilingualTitle = {
  es: string;
  en?: string;
  pt?: string;
};

type MultilingualDescription = {
  es?: string;
  en?: string;
  pt?: string;
};

type MultilingualImage = {
  es?: string;
  en?: string;
  pt?: string;
};

const buildTitle = (season?: AdminSeason): MultilingualTitle => ({
  es: season?.title|| '',
  en: season?.extraData?.en?.title || '',
  pt: season?.extraData?.pt?.title || ''
});

const buildDescription = (season?: AdminSeason): MultilingualDescription => ({
  es: season?.description || '',
  en: season?.extraData?.en?.description || '',
  pt: season?.extraData?.pt?.description || ''
});

const buildImage = (season?: AdminSeason): MultilingualImage => ({
  es: season?.image || '',
  en: season?.extraData?.en?.image || '',
  pt: season?.extraData?.pt?.image || ''
});

const SeasonForm: FC<IProps> = ({ initialData, onSubmit, onCancel, loading = false, isEdit = false }) => {
  const toast = useToast();
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [title, setTitle] = useState<MultilingualTitle>(buildTitle(initialData));
  const [slug, setSlug] = useState<string>(initialData ? (initialData as any).slug || '' : '');
  const [description, setDescription] = useState<MultilingualDescription>(buildDescription(initialData));
  const [image, setImage] = useState<MultilingualImage>(buildImage(initialData));
  const [otherLangs, setOtherLangs] = useState<boolean>(
    !!(initialData?.extraData?.en || initialData?.extraData?.pt)
  );
  const { getTokens } = useSeasonChallengeTasks();

  // Estado genérico para todas las opciones
  const [optionsState, setOptionsState] = useState<Record<string, {value: string, label: string}[]>>({});

  // Función genérica para formatear respuestas
  const formatOptionsResponse = useCallback((response: any[], useNumericalId: boolean = false, useTokenId: boolean = false): {value: string, label: string}[] => {
    if (!Array.isArray(response)) return [];
    if (useTokenId) {
      return response.map((item: any) => ({
        value: String(item.tokenId),
        label: item.name || 'Untitled'
      }));
    }
    return response.map((item: any) => ({
      value: useNumericalId ? String(item.numericalId) : String(item.id),
      label: item.title || item.name || 'Untitled'
    }));
  }, []);

  const loadTokenOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 100,
        name: inputValue || undefined
      };
      const response = await getTokens(params);
      return formatOptionsResponse(response, false);
    } catch (error) {
      console.error('Error loading token options:', error);
      return [];
    }
  }, [getTokens, formatOptionsResponse]);

  const [hasTranslations, setHasTranslations] = useState<boolean>(
    !!(initialData?.title?.en || initialData?.title?.pt || 
       (initialData as any)?.extraData?.en || (initialData as any)?.extraData?.pt)
  );

  const checkForTranslations = () => {
    const hasEnglish = !!(title.en || description.en || image.en);
    const hasPortuguese = !!(title.pt || description.pt || image.pt);
    const hasExistingTranslations = !!(initialData?.extraData?.en || initialData?.extraData?.pt);
    
    return hasEnglish || hasPortuguese || hasExistingTranslations;
  };

  useEffect(() => {
    setHasTranslations(checkForTranslations());
  }, [title, description, image]);

  useEffect(() => {
    const loadAllInitialOptions = async () => {
      try {        
        const tokensResponse = await getTokens({ page: 1, limit: 100 });
        const formattedTokens = formatOptionsResponse(tokensResponse, false, true); // false para id

        setOptionsState({
          token: formattedTokens,
        });
      } catch (error) {
        console.error('Error loading initial options:', error);
      }
    };

    loadAllInitialOptions();
  }, []);

  const useFormMethods = useForm<SeasonFormData>({
    resolver: yupResolver(seasonSchema),
    mode: 'onChange',
    defaultValues: {
      title: initialData?.title || '',
      slug: initialData?.slug || '',
      description: initialData?.description || '',
      image: initialData?.image || '',
      startAt: initialData?.startAt ? toLocalTimezone(initialData.startAt) : new Date(),
      endAt: initialData?.endAt ? toLocalTimezone(initialData.endAt) : new Date(),
      visible: initialData?.visible || false,
      extra_data: initialData ? initialData.extraData : {},
      completionTokenId: initialData?.completionTokenId ? String(initialData.completionTokenId) : undefined,
      coinExpirationDays: initialData?.coinExpirationDays || undefined
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useFormMethods;

  const watchedImage = watch('image');

  const beforeSubmit = async (data: SeasonFormData) => {
    const finalData: SeasonFormData = {
      ...data,
      title: title.es,
      slug: slug,
      description: description.es,
      image: image.es,
      startAt: fromLocalDateTimeToUTC(data.startAt),
      endAt: fromLocalDateTimeToUTC(data.endAt),
      extra_data: {
        ...data.extra_data,
        ...(hasTranslations && {
          en: {
            title: title.en || '',
            description: description.en || '',
            image: image.en || ''
          },
          pt: {
            title: title.pt || '',
            description: description.pt || '',
            image: image.pt || ''
          },
          translated: true
        })
      }
    };
    await onSubmit(finalData);
  };

  return (
    <OlaggForm
      onSubmit={beforeSubmit}
      useFormMethods={useFormMethods}
      style={{
        width: '100%'
      }}
    >
      <VStack color={'white'} w={'full'} maxW='600px' margin='0 auto'>

        {/* Switch para otros idiomas */}
        <Flex
          w="full"
          justify="start"
          align="center"
          style={{ marginBottom: '20px' }}
        >
          <Text color="white" style={{ cursor: 'pointer' }}>
            Otros idiomas
          </Text>
          <Switch
            colorScheme="purple"
            isChecked={otherLangs}
            size="md"
            ml={2}
            onChange={() => setOtherLangs(prev => !prev)}
          />
        </Flex>
        {/* Título en Español */}
        <OlaGGFormInput
          label='Título (Español)'
          controlName='title'
          placeholder='Título de la temporada'
          required={true}
          onChange={(e) => {
            setTitle({ ...title, es: e.target.value });
          }}
        />

        <OlaGGFormInput
          label='Slug'
          controlName='slug'
          placeholder='slug-de-la-temporada'
          required={true}
          onChange={(e) => {
            setSlug(e.target.value);
          }}
        />

        {/* Título en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Título en inglés */}
            <OlaGGFormInput
              label='Título en Inglés'
              controlName='extra_data.en.title'
              placeholder='Season Title in English'
              onChange={(e) => setTitle({ ...title, en: e.target.value })}
            />

            {/* Título en portugués */}
            <OlaGGFormInput
              label='Título en Portugués'
              controlName='extra_data.pt.title'
              placeholder='Título da Temporada em Português'
              onChange={(e) => setTitle({ ...title, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* Descripción */}
        <OlaGGFormInput
          label='Descripción'
          controlName='description'
          placeholder='Descripción de la temporada'
          inputType='textarea'
          onChange={(e) => setDescription({ ...description, es: e.target.value })}
        />

        {/* Descripción en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Descripción en inglés */}
            <OlaGGFormInput
              label='Descripción en Inglés'
              controlName='extra_data.en.description'
              placeholder='Season Description in English'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, en: e.target.value })}
            />

            {/* Descripción en portugués */}
            <OlaGGFormInput
              label='Descripción en Portugués'
              controlName='extra_data.pt.description'
              placeholder='Descrição da Temporada em Português'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* URL de Imagen */}
        <OlaGGFormInput
          label='URL de Imagen'
          controlName='image'
          placeholder='https://ejemplo.com/imagen.jpg'
          onChange={(e) => setImage({ ...image, es: e.target.value })}
        />

        {/* Imagen en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Imagen en inglés */}
            <OlaGGFormInput
              label='URL de Imagen en Inglés'
              controlName='extra_data.en.image'
              placeholder='https://example.com/image-en.jpg'
              onChange={(e) => setImage({ ...image, en: e.target.value })}
            />

            {/* Imagen en portugués */}
            <OlaGGFormInput
              label='URL de Imagen en Portugués'
              controlName='extra_data.pt.image'
              placeholder='https://exemplo.com/imagem-pt.jpg'
              onChange={(e) => setImage({ ...image, pt: e.target.value })}
            />
          </Flex>
        )}

        {/* Vista previa de imagen */}
        {(image.es || image.en || image.pt) && (
          <Box mb={4}>
            <Text color="white" fontSize="sm" mb={2}>Vista previa de imágenes:</Text>
            <HStack align="start" spacing={4}>
              {image.es && (
                <Box>
                  <Text color="white" fontSize="xs" mb={1} fontWeight="bold">Español:</Text>
                  <Image
                    src={image.es}
                    alt="Vista previa español"
                    maxW="300px"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="300px"
                        h="200px"
                        bg="gray.600"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.400">No se pudo cargar la imagen</Text>
                      </Box>
                    }
                  />
                </Box>
              )}

              {image.en && (
                <Box>
                  <Text color="purple.300" fontSize="xs" mb={1} fontWeight="bold">Inglés (EN):</Text>
                  <Image
                    src={image.en}
                    alt="Vista previa inglés"
                    maxW="300px"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="300px"
                        h="200px"
                        bg="gray.600"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.400">Could not load image</Text>
                      </Box>
                    }
                  />
                </Box>
              )}

              {image.pt && (
                <Box>
                  <Text color="green.300" fontSize="xs" mb={1} fontWeight="bold">Portugués (PT):</Text>
                  <Image
                    src={image.pt}
                    alt="Vista previa portugués"
                    maxW="300px"
                    maxH="200px"
                    objectFit="cover"
                    borderRadius="md"
                    fallback={
                      <Box
                        w="300px"
                        h="200px"
                        bg="gray.600"
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.400">Não foi possível carregar a imagem</Text>
                      </Box>
                    }
                  />
                </Box>
              )}
            </HStack>
          </Box>
        )}

        <OlaggFormSelect
          label='Token de Recompoensa por completar la temporada'
          controlName='completionTokenId'
          placeholder='Selecciona un token'
          asyncOptions={loadTokenOptions}
          staticOptions={optionsState['token'] || []}
          isSearchable
          newVersion
          required={false}
        />

        <OlaGGFormInput
          label='Días de expiración de monedas'
          description={'Cantidad de días después de los cuales las monedas de esta temporada expirarán. Dejar vacío para que expiren al finalizar la temporada.'}
          controlName='coinExpirationDays'
          placeholder='14'
        />

        {/* Fechas */}
        <HStack spacing={4} w="full">
          <Box flex={1}>
            
            <OlaGGFormInput
              label='Fecha de Inicio'
              controlName='startAt'
              inputType='datetime'
              description={`Zona horaria: ${currentTimeZone}`}
              datePickerProps={{ backgroundColor: 'white' }}
              required={true}
            />
          </Box>
          <Box flex={1}>
            <OlaGGFormInput
              label='Fecha de Fin'
              controlName='endAt'
              inputType='datetime'
              description={`Zona horaria: ${currentTimeZone}`}
              datePickerProps={{ backgroundColor: 'white' }}
              required={true}
            />
          </Box>
        </HStack>

        {/* Visibilidad */}
        <OlaGGFormInput
          label='Visible'
          controlName='visible'
          inputType='switch'
          required={false}
        />

        <Button
          variant={'filledGradient'}
          type="submit"
          w={'full'}
          style={{
            marginTop: '30px'
          }}
          isLoading={loading}
          loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
        >
          {isEdit ? 'Actualizar Temporada' : 'Crear Temporada'}
        </Button>
      </VStack>
    </OlaggForm>
  );
};

export default SeasonForm;
export { SeasonForm as Form };
